import type { CSSObject } from '@emotion/react'
import {
  Color,
  FontWeight,
  Shadow,
  ShadowElevation,
  Size,
  Spacing,
  Theme,
  Transition,
  Typography,
  ZIndex,
} from '@opallabs/gem'

type CommonArgs = {
  hasError?: boolean
  isOpen?: boolean
  optionCount?: number
  theme: Theme
}

export const inputWrapper: CSSObject = {
  position: 'relative',
  'label + .single-select-value-input': {
    marginTop: Spacing.XS,
  },
}

// The design's label style, the same one `InputLabel` wears.
export const inputLabel = (theme: Theme): CSSObject => ({
  ...Typography.H50,
  color: Color[theme].Text.Default,
})

export const inputContainer = ({
  hasError,
  isOpen,
  theme,
}: CommonArgs): CSSObject => ({
  alignItems: 'center',
  borderStyle: 'solid',
  borderWidth: 1,
  borderRadius: Size.Rounding,
  boxSizing: 'border-box',
  display: 'flex',
  flexFlow: 'row nowrap',
  height: Spacing.XS * 9,
  padding: Spacing.S,
  cursor: 'pointer',
  backgroundColor: Color[theme].Surface.N100,
  borderColor: isOpen
    ? Color[theme].Surface.A200
    : hasError
      ? Color[theme].Surface.R100
      : Color[theme].Surface.N300,
  ...Transition(['border-color', 'box-shadow', 'opacity']),

  '&:disabled': {
    cursor: 'not-allowed',
    opacity: 0.7,
    backgroundColor: Color[theme].Surface.N200,
  },
  '&:focused': {
    borderColor: Color[theme].Surface.A200,
  },
  // used for hover/active overlays on top of the button’s base color
  '&::before': {
    borderRadius: 'inherit',
    content: '""',
    display: 'flex',
    // `-1` compensates for `borderWidth: 1`; without it `::before` is inset
    // by 1 pixel from the button’s outer edge
    inset: -1,
    position: 'absolute',
    zIndex: 0,
    ...Transition(['background-color']),
  },

  '@media (prefers-contrast: more)': {
    fontWeight: FontWeight.Semibold,
  },

  '@media (prefers-reduced-motion: reduced)': {
    transition: 'none',
    '&::before': {
      transition: 'none',
    },
  },
})

export const selectionContent: CSSObject = {
  flex: 1,
  textAlign: 'left',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  display: 'flex',
  alignItems: 'center',
  columnGap: Spacing.XS,
  zIndex: ZIndex.Element.Level1,
}

export const selectionText = ({
  theme,
  isPlaceholder,
}: {
  theme: Theme
  isPlaceholder: boolean
}): CSSObject => ({
  ...Typography.T500,
  color: isPlaceholder ? Color[theme].Text.Gray : Color[theme].Text.Default,
  overflow: 'hidden',
  textOverflow: 'ellipsis',

  '&[aria-invalid="true"]': {
    opacity: 0.5,
  },
})

export const selectionAvatar: CSSObject = {
  display: 'flex',
  width: '100%',
}

export const menu = ({ theme, optionCount }: CommonArgs): CSSObject => ({
  borderBottomLeftRadius: Size.Rounding,
  borderBottomRightRadius: Size.Rounding,
  boxSizing: 'border-box',
  display: 'flex',
  flexFlow: 'column nowrap',
  listStyle: 'none',
  marginBlock: 0,
  maxHeight: optionCount && optionCount > 4 ? 218 : 'unset',
  padding: 0,
  position: 'fixed',
  width: 'max-content',
  top: 0,
  left: 0,
  zIndex: ZIndex.Shroud.Popover,
  backgroundColor: Color[theme].Surface.N000,
  ...Shadow[ShadowElevation.E3],

  // Ensures menu is still interactive when Radix focus trapping is active
  pointerEvents: 'auto',
})

// Workaround for Blink rendering bug
// `overflow` on absolutely-positioned elements with `max-height`
// causes children to be rendered blurry
export const menuContent: CSSObject = {
  height: '100%',
  overflowY: 'auto',
  display: 'flex',
  flexFlow: 'column nowrap',
  rowGap: Spacing.XS,
  padding: `${Spacing.S}px ${Spacing.XS}px ${Spacing.XS}px`,
  margin: 0,
}

export const noOptions: CSSObject = {
  padding: Spacing.S,
  textAlign: 'center',
  ...Typography.T500,
}

export const searchContainer = ({ theme }: CommonArgs): CSSObject => ({
  padding: `${Spacing.S}px ${Spacing.XS}px 0`,

  backgroundColor: Color[theme].Surface.N000,

  '+ ul[role="listbox"]': {
    paddingTop: `${Spacing.XS}px`,
  },
})

export const persistentOption = (
  theme: Theme,
  isHighlighted: boolean
): CSSObject => ({
  ...Typography.T500,
  padding: '6px 8px',
  backgroundColor: isHighlighted
    ? Color[theme].Translucent.A08
    : Color[theme].Surface.N000,
  '&:hover': {
    cursor: 'pointer',
    backgroundColor: Color[theme].Translucent.A08,
  },
})
